import { ArrowDown, ArrowUp, Plus, Save, Trash2, TriangleAlert, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ApiError,
  customScenarioApi,
  whatIfApi,
  type CustomScenario,
  type CustomScenarioDefinition,
  type CustomScenarioLever,
  type CustomScenarioOutput,
  type ScenarioLeverUnit,
  type ScenarioValueUnit,
  type WhatIfOptions,
  type WhatIfPeriod,
} from "../../../services/api";
import { PERIOD_LABELS } from "../scenarioFormat";
import {
  LIMITS,
  cleanName,
  definitionOf,
  draftProblems,
  emptyDefinition,
  fieldProblems,
  moved,
  namesFor,
  newLever,
  newOutput,
  withMetric,
  type FieldProblems,
} from "./customScenarioDraft";

const LEVER_UNITS: Record<ScenarioLeverUnit, string> = {
  percent: "Percent (%)",
  points: "Points",
  currency: "Currency ($)",
  currency_per_hour: "Currency per hour",
  count: "Count",
  hours: "Hours",
  months: "Months",
  seconds: "Seconds",
};

const VALUE_UNITS: Record<ScenarioValueUnit, string> = {
  currency: "Currency ($)",
  currency_per_hour: "Currency per hour",
  hours: "Hours",
  percent: "Percent (%)",
  count: "Count",
  months: "Months",
  seconds: "Seconds",
};

interface CustomScenarioBuilderProps {
  contextId: string;
  contextVersion?: string | null;
  /** The scenario being edited; omitted when creating one. */
  existing?: CustomScenario;
  onSaved: (scenario: CustomScenario) => void;
  onCancel: () => void;
}

function Problem({ text }: { text?: string }) {
  return text ? (
    <small className="builder-field-error" role="alert">
      {text}
    </small>
  ) : null;
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <section className="panel builder-section">
      <div className="panel-heading">
        <div>
          <h2>{title}</h2>
          <p>{hint}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function numberValue(raw: string, fallback: number): number {
  const value = Number(raw);
  return raw.trim() === "" || !Number.isFinite(value) ? fallback : value;
}

/**
 * Builds a What-If scenario from certified metrics, bounded inputs and result
 * formulas. Only the definition is kept; results are always recalculated on
 * the governed baseline, so a saved scenario never shows stale numbers.
 */
export function CustomScenarioBuilder({
  contextId,
  contextVersion,
  existing,
  onSaved,
  onCancel,
}: CustomScenarioBuilderProps) {
  const [draft, setDraft] = useState<CustomScenarioDefinition>(() =>
    existing ? definitionOf(existing) : emptyDefinition(contextId),
  );
  const [options, setOptions] = useState<WhatIfOptions | null>(null);
  const [optionsError, setOptionsError] = useState("");
  const [chosenMetric, setChosenMetric] = useState("");
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<FieldProblems>({ byField: new Map(), general: [] });
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    let active = true;
    whatIfApi
      .options(contextId, contextVersion)
      .then((next) => {
        if (active) setOptions(next);
      })
      .catch((caught) => {
        if (active) {
          setOptionsError(
            caught instanceof ApiError
              ? caught.message
              : "The certified metrics could not be loaded.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [contextId, contextVersion]);

  const unusedMetrics = useMemo(
    () =>
      (options?.metrics ?? []).filter(
        (item) => !draft.metrics.some((metric) => metric.member === item.member),
      ),
    [options, draft.metrics],
  );
  const groupings = useMemo(
    () => (options?.dimensions ?? []).filter((item) => !item.temporal),
    [options],
  );

  function change(next: Partial<CustomScenarioDefinition>) {
    setDraft((previous) => ({ ...previous, ...next }));
  }

  function changeLever(index: number, next: Partial<CustomScenarioLever>) {
    setDraft((previous) => ({
      ...previous,
      levers: previous.levers.map((item, position) =>
        position === index ? { ...item, ...next } : item,
      ),
    }));
  }

  function changeOutput(index: number, next: Partial<CustomScenarioOutput>) {
    setDraft((previous) => ({
      ...previous,
      outputs: previous.outputs.map((item, position) => {
        if (position === index) return { ...item, ...next };
        // One result headlines the scenario.
        return next.emphasis ? { ...item, emphasis: false } : item;
      }),
    }));
  }

  function addMetric() {
    const metric = options?.metrics.find((item) => item.member === chosenMetric);
    if (!metric) return;
    setDraft((previous) => withMetric(previous, metric));
    setChosenMetric("");
  }

  function insertName(index: number, name: string) {
    const formula = draft.outputs[index]?.formula ?? "";
    const spacer = formula && !/[\s(]$/.test(formula) ? " " : "";
    changeOutput(index, { formula: `${formula}${spacer}${name}` });
  }

  async function save() {
    const local = draftProblems(draft);
    setSaveError("");
    if (local.length > 0) {
      setProblems({ byField: new Map(), general: local });
      return;
    }
    setProblems({ byField: new Map(), general: [] });
    setSaving(true);
    try {
      const saved = existing
        ? await customScenarioApi.update(existing.id, draft, existing.revision)
        : await customScenarioApi.create(draft);
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setSaveError(caught.message);
        setProblems(fieldProblems(caught.fieldErrors));
      } else {
        setSaveError("The scenario could not be saved.");
      }
    } finally {
      setSaving(false);
    }
  }

  const at = (field: string) => problems.byField.get(field);
  const placedFields = new Set<string>();
  const placed = (field: string) => {
    placedFields.add(field);
    return at(field);
  };

  const body = (
    <>
      <Section title="About" hint="What the scenario answers, in your own words">
        <div className="form-grid">
          <label>
            Name
            <input
              value={draft.title}
              maxLength={80}
              placeholder="Utilization at our bill rate"
              onChange={(event) => change({ title: event.target.value })}
            />
            <Problem text={placed("title")} />
          </label>
          <label>
            Question
            <input
              value={draft.question}
              maxLength={200}
              placeholder="What is one more point of utilization worth?"
              onChange={(event) => change({ question: event.target.value })}
            />
            <Problem text={placed("question")} />
          </label>
        </div>
      </Section>

      <Section
        title="Certified metrics"
        hint="Read from the governed baseline. Formulas use the name on the right."
      >
        {optionsError && <div className="scenario-error" role="alert">{optionsError}</div>}
        <ul className="builder-list">
          {draft.metrics.map((metric, index) => (
            <li key={metric.member} className="builder-row">
              <span className="builder-row-label">{metric.label || metric.member}</span>
              <label>
                Formula name
                <input
                  className="builder-name"
                  value={metric.name}
                  aria-label={`Formula name for ${metric.label || metric.member}`}
                  onChange={(event) =>
                    change({
                      metrics: draft.metrics.map((item, position) =>
                        position === index ? { ...item, name: cleanName(event.target.value) } : item,
                      ),
                    })
                  }
                />
                <Problem text={placed(`metrics.${index}.name`) ?? placed(`metrics.${index}.member`)} />
              </label>
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${metric.label || metric.member}`}
                onClick={() =>
                  change({ metrics: draft.metrics.filter((_, position) => position !== index) })
                }
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="builder-add">
          <select
            aria-label="Certified metric to add"
            value={chosenMetric}
            disabled={!options || draft.metrics.length >= LIMITS.metrics}
            onChange={(event) => setChosenMetric(event.target.value)}
          >
            <option value="">{options ? "Choose a certified metric" : "Loading metrics…"}</option>
            {unusedMetrics.map((item) => (
              <option key={item.member} value={item.member} title={item.description || item.member}>
                {item.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button secondary"
            disabled={!chosenMetric}
            onClick={addMetric}
          >
            <Plus size={14} /> Add metric
          </button>
        </div>
      </Section>

      <Section
        title="Inputs"
        hint="Inputs to test move the answer; planning assumptions price it. Today uses each input's no-change value."
      >
        {draft.levers.map((lever, index) => (
          <fieldset key={index} className="builder-card" aria-label={`Input ${lever.label}`}>
            <div className="builder-card-head">
              <strong>{lever.label || "Input"}</strong>
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${lever.label || "input"}`}
                onClick={() =>
                  change({ levers: draft.levers.filter((_, position) => position !== index) })
                }
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="builder-grid">
              <label>
                Label
                <input
                  value={lever.label}
                  maxLength={80}
                  onChange={(event) => changeLever(index, { label: event.target.value })}
                />
              </label>
              <label>
                Formula name
                <input
                  className="builder-name"
                  value={lever.id}
                  onChange={(event) => changeLever(index, { id: cleanName(event.target.value) })}
                />
                <Problem text={placed(`levers.${index}.id`)} />
              </label>
              <label>
                Kind
                <select
                  value={lever.group}
                  onChange={(event) =>
                    changeLever(index, { group: event.target.value as CustomScenarioLever["group"] })
                  }
                >
                  <option value="driver">Input to test</option>
                  <option value="assumption">Planning assumption</option>
                </select>
              </label>
              <label>
                Unit
                <select
                  value={lever.unit}
                  onChange={(event) =>
                    changeLever(index, { unit: event.target.value as ScenarioLeverUnit })
                  }
                >
                  {Object.entries(LEVER_UNITS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {(
                [
                  ["minimum", "Minimum"],
                  ["maximum", "Maximum"],
                  ["step", "Step"],
                  ["default", "Starting value"],
                  ...(lever.group === "driver" ? [["neutral", "No-change value"]] : []),
                ] as [keyof CustomScenarioLever, string][]
              ).map(([key, label]) => (
                <label key={key}>
                  {label}
                  <input
                    type="number"
                    value={String(lever[key])}
                    onChange={(event) =>
                      changeLever(index, {
                        [key]: numberValue(event.target.value, lever[key] as number),
                      })
                    }
                  />
                  <Problem text={placed(`levers.${index}.${key}`)} />
                </label>
              ))}
            </div>
            <Problem text={placed(`levers.${index}`)} />
          </fieldset>
        ))}
        <button
          type="button"
          className="button secondary"
          disabled={draft.levers.length >= LIMITS.levers}
          onClick={() => change({ levers: [...draft.levers, newLever(draft)] })}
        >
          <Plus size={14} /> Add input
        </button>
      </Section>

      <Section
        title="Results"
        hint="Arithmetic over metrics, inputs and earlier results: + − × ÷, parentheses, min(), max() and abs()."
      >
        {draft.outputs.map((output, index) => (
          <fieldset key={index} className="builder-card" aria-label={`Result ${output.label}`}>
            <div className="builder-card-head">
              <strong>{output.label || "Result"}</strong>
              <div className="builder-card-actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Move ${output.label} up`}
                  disabled={index === 0}
                  onClick={() => change({ outputs: moved(draft.outputs, index, -1) })}
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Move ${output.label} down`}
                  disabled={index === draft.outputs.length - 1}
                  onClick={() => change({ outputs: moved(draft.outputs, index, 1) })}
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Remove ${output.label || "result"}`}
                  onClick={() =>
                    change({ outputs: draft.outputs.filter((_, position) => position !== index) })
                  }
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="builder-grid">
              <label>
                Label
                <input
                  value={output.label}
                  maxLength={80}
                  onChange={(event) => changeOutput(index, { label: event.target.value })}
                />
              </label>
              <label>
                Formula name
                <input
                  className="builder-name"
                  value={output.id}
                  onChange={(event) => changeOutput(index, { id: cleanName(event.target.value) })}
                />
                <Problem text={placed(`outputs.${index}.id`)} />
              </label>
              <label>
                Unit
                <select
                  value={output.unit}
                  onChange={(event) =>
                    changeOutput(index, { unit: event.target.value as ScenarioValueUnit })
                  }
                >
                  {Object.entries(VALUE_UNITS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                An increase is
                <select
                  value={
                    output.higherIsBetter == null ? "" : output.higherIsBetter ? "good" : "bad"
                  }
                  onChange={(event) =>
                    changeOutput(index, {
                      higherIsBetter:
                        event.target.value === "" ? null : event.target.value === "good",
                    })
                  }
                >
                  <option value="">Neither good nor bad</option>
                  <option value="good">Good news</option>
                  <option value="bad">Bad news</option>
                </select>
              </label>
            </div>
            <label className="builder-formula">
              Formula
              <input
                value={output.formula}
                maxLength={400}
                spellCheck={false}
                placeholder="revenue * (1 + price_change / 100)"
                onChange={(event) => changeOutput(index, { formula: event.target.value })}
              />
              <Problem text={placed(`outputs.${index}.formula`)} />
            </label>
            <div className="builder-names" role="group" aria-label="Names this formula can use">
              {namesFor(draft, index).map((name) => (
                <button
                  type="button"
                  key={name}
                  className="preset-chip"
                  onClick={() => insertName(index, name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <label className="builder-check">
              <input
                type="radio"
                name="headline-result"
                checked={output.emphasis}
                onChange={() => changeOutput(index, { emphasis: true })}
              />
              Headline result (drives the headline and the bridge)
            </label>
          </fieldset>
        ))}
        <button
          type="button"
          className="button secondary"
          disabled={draft.outputs.length >= LIMITS.outputs}
          onClick={() => change({ outputs: [...draft.outputs, newOutput(draft)] })}
        >
          <Plus size={14} /> Add result
        </button>
      </Section>

      <Section title="Options" hint="How the scenario can be sliced and where it starts">
        <div className="form-grid">
          <label>
            Default period
            <select
              value={draft.defaultPeriod}
              onChange={(event) => change({ defaultPeriod: event.target.value as WhatIfPeriod })}
            >
              {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="builder-groupings">
            <legend>Break down by</legend>
            {groupings.length === 0 && <small>No certified groupings for this source.</small>}
            {groupings.map((item) => {
              const position = draft.breakdowns.indexOf(item.member);
              const checked = position >= 0;
              return (
                <label key={item.member} className="builder-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!checked && draft.breakdowns.length >= LIMITS.breakdowns}
                    onChange={() =>
                      change({
                        breakdowns: checked
                          ? draft.breakdowns.filter((member) => member !== item.member)
                          : [...draft.breakdowns, item.member],
                      })
                    }
                  />
                  {item.label}
                  {checked && <Problem text={placed(`breakdowns.${position}`)} />}
                </label>
              );
            })}
          </fieldset>
        </div>
      </Section>
    </>
  );

  // Anything the server reported against a field the form does not show.
  const unplaced = [...problems.byField.entries()]
    .filter(([field]) => !placedFields.has(field))
    .map(([, message]) => message);
  const summary = [...problems.general, ...unplaced];

  return (
    <div className="content-stack custom-builder">
      <section className="panel builder-header">
        <div>
          <span className="eyebrow dark">{existing ? "Edit custom scenario" : "New custom scenario"}</span>
          <h2>{draft.title || "Untitled scenario"}</h2>
          <p>Only you can see your custom scenarios. Results are simulated, never certified.</p>
        </div>
        <div className="builder-actions">
          <button type="button" className="button secondary" onClick={onCancel} disabled={saving}>
            <X size={14} /> Cancel
          </button>
          <button
            type="button"
            className="button primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? <span className="button-spinner" /> : <Save size={14} />}
            {existing ? "Save changes" : "Save scenario"}
          </button>
        </div>
      </section>

      {(saveError || summary.length > 0) && (
        <div className="scenario-error builder-summary" role="alert">
          <TriangleAlert size={16} />
          <div>
            {saveError && <strong>{saveError}</strong>}
            {summary.length > 0 && (
              <ul>
                {summary.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {body}
    </div>
  );
}
